// The parent half of form 1.6's storage bridge. The form runs in a sandboxed iframe with no origin, session or
// network of its own (public/benchmark/bridge.js); it asks this page for everything by postMessage. This side is
// the trust boundary: only messages from that frame, only a fixed set of ops, and only this case's and this
// reviewer's own documents. Payloads are never logged: they hold customer text.

import { isAxiosError } from 'axios';
import { goldenService } from '@/services/golden.service';
import type { GoldenCase, GoldenRecord } from '@/types/golden.types';

/** Form 1.6's own light/dark key; the frame can't keep it, so this page does. */
export const THEME_KEY = 'reply-review-theme';
export const WITHDRAWN_MESSAGE = 'This case was withdrawn; your answer was not saved.';

const OPS = ['case', 'whoAmI', 'getDoc', 'setDoc', 'listDocs', 'theme', 'nav'] as const;
type BridgeOp = (typeof OPS)[number];

export type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'submitted' }
  | { kind: 'failed'; withdrawn: boolean };

export interface BridgeSession {
  email: string;
  isReviewer: boolean;
  isEditor: boolean;
}

export interface BridgeContext {
  caseData: GoldenCase;
  brandName: string;
  session: BridgeSession;
  onSaveState: (state: SaveState) => void;
  onNav: (to: 'next' | 'home') => void;
}

/** An error code form 1.6 understands, carried back to the frame. */
export class BridgeError extends Error {
  readonly code: string;
  constructor(code: string, message = '') {
    super(message);
    this.code = code;
  }
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The API's error as the form's code: the body's own code when it has one, otherwise by HTTP status. */
export function bridgeErrorCode(error: unknown): string {
  if (error instanceof BridgeError) return error.code;
  if (!isAxiosError(error) || !error.response) return 'unavailable';
  const body: unknown = error.response.data;
  const code = isPlainObject(body)
    ? (body.code ?? (Array.isArray(body.errors) && isPlainObject(body.errors[0]) ? body.errors[0].code : undefined))
    : undefined;
  if (typeof code === 'string' && code) return code;
  const status = error.response.status;
  return status === 403 ? 'not_reviewer' : status === 400 ? 'invalid_argument' : status === 409 ? 'withdrawn' : 'unavailable';
}

function bridgeErrorMessage(error: unknown, code: string): string {
  if (code === 'withdrawn') return WITHDRAWN_MESSAGE;
  if (isAxiosError(error) && isPlainObject(error.response?.data) && typeof error.response.data.message === 'string') {
    return error.response.data.message;
  }
  return error instanceof BridgeError ? error.message : '';
}

/** The documents form 1.6 reads and writes, as paths: only the reviewer's own, and only this case's answer. */
type Doc = { kind: 'all-reviewers' } | { kind: 'reviewer-state' } | { kind: 'answers' } | { kind: 'answer' };

function resolvePath(path: unknown, ctx: BridgeContext): Doc {
  if (typeof path !== 'string') throw new BridgeError('invalid_argument');
  const parts = path.split('/');
  if (parts[0] !== 'answers') throw new BridgeError('invalid_argument');
  if (parts.length === 1) return { kind: 'all-reviewers' };
  if (parts[1] !== ctx.session.email) throw new BridgeError('invalid_argument');
  if (parts.length === 2) return { kind: 'reviewer-state' };
  if (parts[2] === 'cases' && parts.length === 3) return { kind: 'answers' };
  if (parts[2] === 'cases' && parts.length === 4 && parts[3] === ctx.caseData._id) return { kind: 'answer' };
  throw new BridgeError('invalid_argument');
}

async function saveAnswer(record: GoldenRecord, ctx: BridgeContext): Promise<null> {
  ctx.onSaveState({ kind: 'saving' });
  try {
    await goldenService.saveAnswer(ctx.caseData._id, record);
  } catch (error) {
    ctx.onSaveState({ kind: 'failed', withdrawn: bridgeErrorCode(error) === 'withdrawn' });
    throw error;
  }
  ctx.onSaveState({ kind: record.status === 'done' ? 'submitted' : 'saved' });
  return null;
}

export async function handleBridgeOp(op: BridgeOp, args: unknown[], ctx: BridgeContext): Promise<unknown> {
  switch (op) {
    case 'case': {
      let theme: string | null = null;
      try {
        theme = localStorage.getItem(THEME_KEY);
      } catch {
        // Not readable here: the form follows the system theme.
      }
      return { case: ctx.caseData, brandName: ctx.brandName, theme, readOnly: !ctx.session.isReviewer };
    }
    case 'whoAmI':
      return { id: ctx.session.email, ...ctx.session };
    case 'getDoc': {
      const doc = resolvePath(args[0], ctx);
      if (doc.kind === 'reviewer-state') return goldenService.getReviewerState();
      if (doc.kind === 'answer') return goldenService.getAnswer(ctx.caseData._id);
      throw new BridgeError('invalid_argument');
    }
    case 'setDoc': {
      const doc = resolvePath(args[0], ctx);
      const data = args[1];
      if (!isPlainObject(data)) throw new BridgeError('invalid_argument');
      if (doc.kind === 'answer') return saveAnswer(data, ctx);
      if (doc.kind !== 'reviewer-state') throw new BridgeError('invalid_argument');
      await goldenService.saveReviewerState(data);
      return null;
    }
    case 'listDocs': {
      const doc = resolvePath(args[0], ctx);
      // Only editors' overview lists every reviewer; the form gets no one else's answers.
      if (doc.kind === 'all-reviewers') return [];
      if (doc.kind !== 'answers') throw new BridgeError('invalid_argument');
      const mine = await goldenService.myAnswers();
      // The frame holds one case, so it gets that case's answer only.
      return mine.filter((row) => row.caseId === ctx.caseData._id).map((row) => ({ id: row.caseId, data: row.record }));
    }
    case 'theme': {
      if (args[0] !== 'light' && args[0] !== 'dark') throw new BridgeError('invalid_argument');
      try {
        localStorage.setItem(THEME_KEY, args[0]);
      } catch {
        // Not remembered on this device.
      }
      return null;
    }
    case 'nav':
      ctx.onNav(args[0] === 'next' ? 'next' : 'home');
      return null;
  }
}

/**
 * The window "message" listener for one frame. A message counts only when it comes from that frame's window and
 * names a known op; anything else is dropped without a reply.
 */
export function createBridgeListener(getFrame: () => Window | null | undefined, getContext: () => BridgeContext) {
  return (event: MessageEvent) => {
    const frame = getFrame();
    if (!frame || event.source !== frame) return;
    const message: unknown = event.data;
    if (!isPlainObject(message) || message.benchmark !== 1 || typeof message.id !== 'number') return;
    if (!OPS.includes(message.op as BridgeOp) || !Array.isArray(message.args)) return;
    const { id } = message;
    // An opaque-origin frame can only be addressed as "*"; the reply goes to that one window.
    const reply = (body: Record<string, unknown>) => frame.postMessage({ benchmark: 1, id, ...body }, '*');
    handleBridgeOp(message.op as BridgeOp, message.args, getContext()).then(
      (data) => reply({ ok: true, data: data ?? null }),
      (error: unknown) => {
        const code = bridgeErrorCode(error);
        reply({ ok: false, code, message: bridgeErrorMessage(error, code) });
      },
    );
  };
}
